import { useMutation, useQuery } from "@apollo/client";
import {
	Grid,
	Typography,
	IconButton,
	Button,
	TextareaAutosize,
	Fab,
	ImageList,
	ImageListItem,
	Box,
	CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Machine } from "../graphql/querys/machine";
import { MuiFileInput } from "mui-file-input";
import CloseIcon from "@mui/icons-material/Close";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import { CREATE_INTERVENTION } from "../graphql/mutation/createIntervention";
import { uploadInterventionPhotos } from "../services/intervention-photo-upload";

export default function RepairScreen() {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const { loading, error, data, refetch } = useQuery(Machine, {
		variables: { id },
	});
	const [createIntervention, { data: interventionData, loading: mutationLoading }] =
		useMutation(CREATE_INTERVENTION);
	useEffect(() => {
		if (interventionData) {
			navigate(`/machines-mobile/${id}`);
		}
	}, [interventionData, id, navigate]);

	// State for images and observations
	const [previewImages, setPreviewImages] = useState([]);
	const [imgFiles, setImgFiles] = useState([]);
	const [observations, setObservations] = useState("");
	const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
	const [uploadError, setUploadError] = useState("");

	// Create the intervention
	const handleCreateIntervention = async () => {
		setUploadError("");
		setIsUploadingPhotos(true);

		try {
			const photoUrls = await uploadInterventionPhotos(imgFiles);
			await createIntervention({
				variables: {
					input: {
						type: "REPAIR",
						machineId: id,
						photosTaken: photoUrls,
						obs: observations,
					},
				},
			});
		} catch (error) {
			setUploadError(error?.message || "Não foi possível guardar a reparação.");
		} finally {
			setIsUploadingPhotos(false);
		}
	};

	// Handle image uploads
	const handleImageChange = (newFiles) => {
		const files = Array.isArray(newFiles)
			? newFiles
			: newFiles
				? [newFiles]
				: [];
		setImgFiles(files);
		setPreviewImages(files.map((file) => URL.createObjectURL(file)));
	};

	// Remove image
	const handleRemoveImage = (indexToRemove) => {
		setPreviewImages(previewImages.filter((_, index) => index !== indexToRemove));
		setImgFiles(imgFiles.filter((_, index) => index !== indexToRemove));
	};

	if (loading || mutationLoading || isUploadingPhotos) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box p={4} bgcolor="#f4f4f4" minHeight="100vh">
			<Fab
				onClick={() => {
					navigate(`/machines-mobile/${id}`);
				}}
				sx={{
					margin: 0,
					top: 20,
					right: 20,
					position: "fixed",
				}}
				color="primary"
				aria-label={t("ACTIONS.BACK")}>
				<FirstPageIcon />
			</Fab>

			<Typography variant="h5" gutterBottom>
				{t("INTERVENTIONS.REPAIR_TITLE")}
			</Typography>

			{/* Observations Text Area */}
			<Grid item mt="3vh">
				<Typography variant="subtitle2">
					{t("INTERVENTIONS.OBSERVATIONS")}
				</Typography>
				<TextareaAutosize
					minRows={5}
					value={observations}
					onChange={(e) => setObservations(e.target.value)}
					style={{
						width: "100%",
						border: "1px solid #ccc",
						borderRadius: "10px",
						padding: "10px",
						marginTop: "10px",
						fontSize: "16px",
						fontFamily: "Arial",
					}}
					placeholder={t("INTERVENTIONS.OBSERVATIONS_PLACEHOLDER")}
				/>
			</Grid>

			{/* File Input Section */}
			<Grid item mt={5}>
				<MuiFileInput
					value={imgFiles}
					label={t("INTERVENTIONS.ADD_PHOTOS")}
					sx={{ width: "100%" }}
					multiple
					onChange={handleImageChange}
				/>
			</Grid>

			{/* Image Previews */}
			{previewImages.length > 0 && (
				<Box mt={4}>
					<Typography variant="subtitle2" gutterBottom>
						{t("INTERVENTIONS.PHOTOS_PREVIEW")}
					</Typography>
					<ImageList cols={3} gap={10}>
						{previewImages.map((imageData, index) => (
							<ImageListItem key={index}>
								<img src={imageData} alt={`Preview ${index}`} />
								<IconButton
									aria-label={t("ACTIONS.DELETE")}
									onClick={() => handleRemoveImage(index)}
									style={{
										position: "absolute",
										top: 5,
										right: 5,
										color: "red",
									}}>
									<CloseIcon />
								</IconButton>
							</ImageListItem>
						))}
					</ImageList>
				</Box>
			)}
			{uploadError && (
				<Typography color="error" mt={2}>
					{uploadError}
				</Typography>
			)}

			{/* Save Button */}
			<Box mt={5} mb={5} textAlign="center">
				<Button
					variant="contained"
					onClick={handleCreateIntervention}
				disabled={mutationLoading || isUploadingPhotos}
					sx={{
						width: "50%",
						padding: "10px",
					}}>
					{mutationLoading ? <CircularProgress size={24} /> : t("ACTIONS.SAVE")}
				</Button>
			</Box>
		</Box>
	);
}
